import { VerityAssessmentType } from "@verity/database/prisma";
import i18nPackage from "../../../i18n/i18n";
import ConfirmationBlock, {
	CommonConfirmationBlockProps,
	CompactVariantProp,
	ConfirmationBlockSkeleton,
	ConfirmationBlockVariant,
	ConfirmationRow,
	RadioVariantProp,
} from "./ConfirmationBlock";
import { formatLiveSessionDate, formatLiveSessionTime, spansMultipleDays } from "../../live/liveHelper";
import AssessmentDetailsIcon from "../../images/confirmation/assessment-details.svg?react";
import AddInstructionIcon from "../../images/confirmation/add-instruction.svg?react";
import { CoreFileCard, CoreFileItem } from "../../../core";

type QuizQuestionBlockProps = {
	totalQuestions: number;
	totalPoints: number;
	icon?: React.ReactNode;
} & CommonConfirmationBlockProps;

export function QuizQuestionBlock({ totalQuestions, totalPoints, icon, ...props }: QuizQuestionBlockProps) {
	return (
		<ConfirmationBlock
			variant="radio"
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.questions")}
			icon={icon}
			targetPageName="questions"
		>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.totalQuestions")}
				description={String(totalQuestions)}
			/>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.totalPoints")}
				description={String(totalPoints)}
			/>
		</ConfirmationBlock>
	);
}

type AssessmentTypeBlockProps = {
	type: VerityAssessmentType;
	icon?: React.ReactNode;
	variant?: ConfirmationBlockVariant;
} & CommonConfirmationBlockProps;

export function AssessmentTypeBlock({ type, icon, variant = "radio", ...props }: AssessmentTypeBlockProps) {
	const key = type.toLowerCase() as "assignment" | "quiz";
	const isCompact = variant === "compact";

	const description = i18nPackage.t(`editQuizPage.confirmationBlockTSX.${key}`);

	if (props.loading) return <ConfirmationBlockSkeleton />;

	return (
		<ConfirmationBlock
			variant="radio"
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentType")}
			icon={icon}
			targetPageName={"assessmentType"}
		>
			<ConfirmationRow
				title={isCompact ? description : i18nPackage.t("editQuizPage.confirmationBlockTSX.type")}
				description={isCompact ? undefined : description}
			/>
		</ConfirmationBlock>
	);
}

function convertNumberToDuration(duration: number | null): { hour: number; minute: number } | null {
	if (duration === null) return null;
	if (duration <= 0) return null;
	const hour = Math.floor(duration / 60);
	const minute = duration % 60;

	return { hour, minute };
}

type BaseVerityAssessmentDetailsBlockProps = CommonConfirmationBlockProps;

type RadioVerityAssessmentDetailsBlockProps = BaseVerityAssessmentDetailsBlockProps &
	RadioVariantProp & {
		courseName?: string;
		syncWithGradebook?: boolean;
		moduleName?: string;
		title: string;
		description?: string;
		startDate: Date;
		endDate: Date;
		lateSubmissionDate: Date | null;
		duration: number | null;
		numAttemptsAllowed: number;
		allowedFileTypes?: string[];
		assignmentMaxScore?: number;
		scoringStrategy?: string;
		showScoreOnSubmission?: boolean;
	};

type CompactVerityAssessmentDetailsBlockProps = BaseVerityAssessmentDetailsBlockProps & CompactVariantProp;

type VerityAssessmentDetailsBlockProps =
	| RadioVerityAssessmentDetailsBlockProps
	| CompactVerityAssessmentDetailsBlockProps;

function RadioVerityAssessmentDetailsBlock({
	courseName,
	syncWithGradebook,
	moduleName,
	title,
	description,
	startDate,
	endDate,
	lateSubmissionDate,
	duration,
	numAttemptsAllowed,
	allowedFileTypes,
	assignmentMaxScore,
	scoringStrategy,
	showScoreOnSubmission,
	...props
}: RadioVerityAssessmentDetailsBlockProps) {
	const multipleDates = spansMultipleDays(startDate, endDate);
	const durationObj = convertNumberToDuration(duration);
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDetails")}
			icon={<AssessmentDetailsIcon />}
			targetPageName="assessmentDetails"
		>
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentTitle")} description={title} />
			{description && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDescription")}
					description={description}
					descriptionTooltipType="viewMore"
				/>
			)}
			{courseName && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.course")}
					description={courseName}
					tooltipType="overflow"
				/>
			)}
			{moduleName && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.module")}
					description={moduleName}
					tooltipType="overflow"
				/>
			)}
			{syncWithGradebook !== undefined && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.syncWithGradebook")}
					description={
						syncWithGradebook
							? i18nPackage.t("editQuizPage.confirmationBlockTSX.yes")
							: i18nPackage.t("editQuizPage.confirmationBlockTSX.no")
					}
				/>
			)}
			{typeof assignmentMaxScore === "number" && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assignmentMaxPoints")}
					description={String(assignmentMaxScore)}
				/>
			)}
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDueDate")}
				description={
					multipleDates
						? `${formatLiveSessionDate(startDate)} ${formatLiveSessionTime(startDate)} to ${formatLiveSessionDate(endDate)} ${formatLiveSessionTime(endDate)}`
						: `${formatLiveSessionDate(startDate)} ${formatLiveSessionTime(startDate)} to ${formatLiveSessionTime(endDate)}`
				}
				tooltipType="overflow"
			/>
			{lateSubmissionDate && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.lateSubmission")}
					description={formatLiveSessionDate(lateSubmissionDate)}
				/>
			)}
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDuration")}
				description={
					durationObj
						? `${durationObj.hour > 0 ? `${durationObj.hour}h ` : ""}${durationObj.minute}m`
						: i18nPackage.t("editQuizPage.confirmationBlockTSX.unlimited")
				}
			/>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.numAttempts")}
				description={
					numAttemptsAllowed > 0 ? numAttemptsAllowed : i18nPackage.t("editQuizPage.confirmationBlockTSX.unlimited")
				}
			/>
			{scoringStrategy && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.gradeBook")}
					description={scoringStrategy}
				/>
			)}
			{showScoreOnSubmission !== undefined && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.showScoreOnSubmission")}
					description={
						showScoreOnSubmission
							? i18nPackage.t("editQuizPage.confirmationBlockTSX.yes")
							: i18nPackage.t("editQuizPage.confirmationBlockTSX.no")
					}
				/>
			)}
			{allowedFileTypes && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.allowedFileTypes")}
					description={
						allowedFileTypes.length > 0
							? allowedFileTypes.join(", ")
							: i18nPackage.t("editQuizPage.confirmationBlockTSX.allFileTypes")
					}
				/>
			)}
		</ConfirmationBlock>
	);
}

function CompactVerityAssessmentDetailsBlock(props: CompactVerityAssessmentDetailsBlockProps) {
	return (
		<ConfirmationBlock
			{...props}
			targetPageName="assessmentDetails"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDetails")}
			buttonVariant={"link"}
			onEdit={props.onEdit}
			buttonText={i18nPackage.t("editQuizPage.confirmationBlockTSX.manageAssessmentDetails")}
		/>
	);
}

export function VerityAssessmentDetailsBlock(props: VerityAssessmentDetailsBlockProps) {
	if (props.loading) return <ConfirmationBlockSkeleton />;

	if (props.variant === "radio") {
		return <RadioVerityAssessmentDetailsBlock {...props} />;
	}

	return <CompactVerityAssessmentDetailsBlock {...props} />;
}

type FileWithErrorMessage = { file: CoreFileItem; errorMessage?: string };

type VerityAssignmentInstructionsBlockProps = {
	files: FileWithErrorMessage[];
	children?: React.ReactNode;
} & CommonConfirmationBlockProps;

export function VerityAssignmentInstructionsBlock({
	files,
	children,
	...props
}: VerityAssignmentInstructionsBlockProps) {
	return (
		<ConfirmationBlock
			variant="radio"
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.instructions")}
			icon={<AddInstructionIcon />}
			targetPageName="instructions"
		>
			{children}
			{files.length > 0 &&
				files.map((fileWithError, index) => (
					<CoreFileCard
						key={index}
						file={fileWithError.file}
						errorMessage={fileWithError.errorMessage}
						showOpenInNewTab
					/>
				))}
		</ConfirmationBlock>
	);
}
